import type { FC, ReactNode } from 'react'
import type { Service } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { CtaLink } from '@/components/generals/cta-link'
import { Mark } from '@/components/generals/mark'
import { SectionLabel } from '@/components/generals/section-label'

interface ServicesOverviewProps {
    services: Service[]
}

const FEATURE_LAYOUT = [
    { cell: 'md:col-span-7', aspect: 'aspect-[16/10]' },
    { cell: 'md:col-span-5 md:mt-24', aspect: 'aspect-[4/3]' }
]

const ServicesOverview: FC<ServicesOverviewProps> = ({ services }): ReactNode => {
    const featured = services.slice(0, 2)
    const rest = services.slice(2)

    return (
        <section id='services' aria-labelledby='services-title' data-nav-theme='light' className='bg-cream text-charcoal'>
            <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
                    <div data-reveal className='lg:col-span-8'>
                        <SectionLabel>What we do / 01</SectionLabel>
                        <h2 id='services-title' className='mt-6 font-heading type-section'>
                            <span className='block'>Strategy.</span>
                            <span className='block md:pl-[8%]'>Creativity.</span>
                            <span className='block md:pl-[16%]'><Mark>Performance.</Mark></span>
                        </h2>
                    </div>
                    <div data-reveal className='space-y-6 lg:col-span-4'>
                        <p className='text-[17px] leading-relaxed text-charcoal/85'>
                            From positioning your brand to executing campaigns and optimizing results, we create marketing solutions designed around your business objectives.
                        </p>
                        <CtaLink href='/services' variant='text'>All services</CtaLink>
                    </div>
                </div>

                {featured.length > 0 && (
                    <div className='mt-14 grid gap-12 md:mt-20 md:grid-cols-12 md:gap-8'>
                        {featured.map((service, index) => {
                            const cell = FEATURE_LAYOUT[index]

                            return (
                                <article key={service.id} data-reveal className={cn('group', cell.cell)}>
                                    <Link href={`/services/${service.slug}`} className='block rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-coral'>
                                        <div data-reveal-image className={cn('relative overflow-hidden bg-stone', cell.aspect)}>
                                            {service.image && (
                                                <Image
                                                    src={service.image}
                                                    alt={service.imageAlt}
                                                    fill
                                                    sizes='(min-width: 768px) 55vw, 100vw'
                                                    className='object-cover transition-[scale] duration-[1400ms] ease-[var(--ease-out-expo)] group-hover:scale-[1.04]'
                                                />
                                            )}
                                            <span className='absolute left-0 top-0 bg-coral px-3.5 py-2.5 font-heading font-wide text-2xl font-black leading-none tracking-[-0.04em] md:text-3xl'>
                                                {pad(index + 1)}
                                            </span>
                                        </div>
                                        <div className='mt-6 flex items-start justify-between gap-6'>
                                            <div>
                                                <h3 className='font-heading font-wide text-[clamp(1.35rem,2vw,1.85rem)] font-black uppercase leading-[0.98] tracking-[-0.02em]'>
                                                    {service.title}
                                                </h3>
                                                <p className='mt-3 max-w-md text-[15px] leading-relaxed text-charcoal/80'>{service.summary}</p>
                                            </div>
                                            <span className='flex size-12 shrink-0 items-center justify-center rounded-sm border border-charcoal transition-colors duration-300 group-hover:bg-charcoal group-hover:text-cream'>
                                                <ArrowUpRight aria-hidden='true' className='size-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                            </span>
                                        </div>
                                    </Link>
                                </article>
                            )
                        })}
                    </div>
                )}

                {rest.length > 0 && (
                    <ul className='mt-16 border-t border-charcoal md:mt-24'>
                        {rest.map((service, index) => (
                            <li key={service.id} data-reveal className='border-b border-charcoal/25'>
                                <Link
                                    href={`/services/${service.slug}`}
                                    className='group relative grid grid-cols-[3rem_1fr_auto] items-center gap-4 overflow-hidden py-7 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral md:grid-cols-[6rem_1.2fr_1fr_auto] md:gap-8 md:py-9 xl:grid-cols-[6rem_1.2fr_1fr_10rem_auto]'
                                >
                                    <span aria-hidden='true' className='absolute inset-0 -z-0 origin-left scale-x-0 bg-coral transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100 group-focus-visible:scale-x-100' />

                                    <span className='relative font-heading font-wide text-xl font-black tracking-[-0.03em] md:pl-4 md:text-3xl'>
                                        {pad(index + 3)}
                                    </span>
                                    <span className='relative font-heading font-wide text-[clamp(1.2rem,2vw,1.85rem)] font-black uppercase leading-[0.98] tracking-[-0.02em]'>
                                        {service.title}
                                    </span>
                                    <span className='relative hidden text-[15px] leading-relaxed text-charcoal/85 md:block'>
                                        {service.summary}
                                    </span>

                                    <span aria-hidden='true' className='relative hidden h-24 w-40 overflow-hidden bg-charcoal/10 transition-[clip-path] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] [clip-path:inset(50%_0_50%_0)] group-hover:[clip-path:inset(0_0_0_0)] group-focus-visible:[clip-path:inset(0_0_0_0)] xl:block'>
                                        {service.image && <Image src={service.image} alt='' fill sizes='160px' className='object-cover' />}
                                    </span>

                                    <span className='relative flex size-11 items-center justify-center rounded-sm border border-charcoal transition-colors duration-300 group-hover:bg-charcoal group-hover:text-cream md:mr-4'>
                                        <ArrowRight aria-hidden='true' className='size-5 transition-transform duration-300 group-hover:translate-x-0.5' />
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    )
}

export default ServicesOverview
